import React, { useEffect, useState } from "react";
import Marquee from "react-fast-marquee";
import { Star, MessageSquareQuote, ArrowUpRight } from "lucide-react";
import { api, GOOGLE_REVIEW_URL, GOOGLE_REVIEWS } from "../api";
import { Reveal } from "./motion";

function Stars({ n }) {
    return (
        <div className="flex gap-0.5" aria-label={`${n} out of 5 stars`}>
            {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} className={`h-4 w-4 ${i <= n ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />
            ))}
        </div>
    );
}

function ReviewCard({ r, index }) {
    return (
        <figure className="mx-3 w-[320px] shrink-0 rounded-2xl border border-border bg-white p-6 shadow-sm" data-testid={`review-card-${index}`}>
            <Stars n={r.rating} />
            <blockquote className="mt-4 line-clamp-5 text-sm leading-relaxed text-slate-700">"{r.text}"</blockquote>
            <figcaption className="mt-5 flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-navy font-display text-sm font-semibold text-white">
                    {r.name.charAt(0)}
                </span>
                <div>
                    <p className="text-sm font-semibold text-navy">{r.name}</p>
                    <p className="text-xs text-steel">{r.date} · Google review</p>
                </div>
            </figcaption>
        </figure>
    );
}

export default function Reviews() {
    const [reviews, setReviews] = useState(GOOGLE_REVIEWS);

    useEffect(() => {
        api.get("/settings/reviews")
            .then((r) => { if (r.data.reviews?.length) setReviews(r.data.reviews); })
            .catch(() => {});
    }, []);

    const has = reviews.length > 0;
    return (
        <section className="border-t border-border bg-mist py-20 lg:py-24" data-testid="reviews-section">
            <div className="mx-auto max-w-7xl px-5 lg:px-10">
                <div className="flex flex-wrap items-end justify-between gap-6">
                    <div>
                        <Reveal>
                            <span className="font-mono-label text-accent">Patient Feedback</span>
                        </Reveal>
                        <Reveal>
                            <h2 className="mt-4 max-w-xl font-display text-3xl font-medium leading-tight text-navy sm:text-4xl">
                                What patients say on Google
                            </h2>
                        </Reveal>
                    </div>
                    <Reveal delay={0.1}>
                        <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noopener noreferrer" data-testid="leave-review-btn"
                            className="group inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-accent hover:shadow-lg">
                            <MessageSquareQuote className="h-4 w-4" aria-hidden="true" />
                            Share your experience on Google
                            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </a>
                    </Reveal>
                </div>
            </div>
            {has && (
                <Reveal delay={0.15} className="mt-12">
                    <Marquee speed={35} gradient={false} pauseOnHover autoFill>
                        {reviews.map((r, i) => <ReviewCard key={i} r={r} index={i} />)}
                    </Marquee>
                </Reveal>
            )}
        </section>
    );
}
